import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useSite } from '@/app/providers/SiteProvider';
import { getMediaPublicUrl } from '@/features/media/lib/getMediaPublicUrl';
import type { PostGridBlock, PostCardBlock } from '@/types/blocks';

export interface PostGridItem {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  published_at: string | null;
  imageUrl: string | null;
}

type ContentBlock = {
  type?: unknown;
  content?: unknown;
  html?: unknown;
  src?: unknown;
  bgImageUrl?: unknown;
  images?: unknown;
  children?: unknown;
  columns?: unknown;
};

interface ContentSeo {
  og_image?: string;
}

function getContentSeo(value: unknown): ContentSeo {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const record = value as Record<string, unknown>;
  const ogImage =
    typeof record.og_image === 'string'
      ? record.og_image
      : typeof record.image === 'string'
        ? record.image
        : '';
  return { og_image: ogImage };
}

function isContentBlock(value: unknown): value is ContentBlock {
  return typeof value === 'object' && value !== null;
}

function collectContentBlocks(value: unknown): ContentBlock[] {
  if (Array.isArray(value)) return value.flatMap(collectContentBlocks);
  if (!isContentBlock(value)) return [];

  const nested = [
    ...collectContentBlocks(value.children),
    ...collectContentBlocks(value.columns),
  ];

  return [value, ...nested];
}

function getContentImage(content: unknown): string | null {
  for (const block of collectContentBlocks(content)) {
    if (typeof block.src === 'string' && block.src) return block.src;
    if (typeof block.bgImageUrl === 'string' && block.bgImageUrl) return block.bgImageUrl;

    if (Array.isArray(block.images)) {
      const image = block.images.find(
        (item): item is { src: string } => isContentBlock(item) && typeof item.src === 'string' && item.src.length > 0,
      );
      if (image) return image.src;
    }

    if (typeof block.html === 'string') {
      const match = block.html.match(/<img[^>]+src=["']([^"']+)["']/i);
      if (match?.[1]) return match[1];
    }
  }

  return null;
}

function toPlainText(value: string): string {
  return value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

function getContentExcerpt(content: unknown): string | null {
  const text = collectContentBlocks(content)
    .flatMap((block) => [block.content, block.html])
    .filter((value): value is string => typeof value === 'string')
    .map(toPlainText)
    .filter(Boolean)
    .join(' ')
    .slice(0, 220)
    .trim();

  return text || null;
}

export interface PostGridPage {
  items: PostGridItem[];
  totalCount: number;
}

/**
 * Busca posts publicados do site para o widget de grid: filtra por categoria
 * (via post_terms) e pagina em fatias de `count` itens (página 1 = mais
 * recentes; páginas seguintes = posts mais antigos, sempre ordenado por
 * published_at desc). Resolve a thumbnail em uma segunda consulta porque
 * posts.thumbnail_id não tem FK declarada para media_items.
 */
export function usePostGridPosts(siteId: string | null | undefined, count: number, categoryId: string | null, page: number = 1) {
  return useQuery({
    queryKey: ['post-grid', siteId, count, categoryId, page],
    queryFn: async (): Promise<PostGridPage> => {
      if (!siteId) return { items: [], totalCount: 0 };
      const limit = Math.max(1, Math.min(count, 24));
      const from = (Math.max(1, page) - 1) * limit;
      const to = from + limit - 1;
      const nowIso = new Date().toISOString();

      const baseCols = 'id, title, slug, excerpt, published_at, thumbnail_id, content, seo, og';
      let rows: {
        id: string;
        title: string;
        slug: string;
        excerpt: string | null;
        published_at: string | null;
        thumbnail_id: string | null;
        content: unknown;
        seo: unknown;
        og: unknown;
      }[];
      let totalCount = 0;

      if (categoryId) {
        const { data, error, count: total } = await supabase
          .from('posts')
          .select(`${baseCols}, post_terms!inner(term_id)`, { count: 'exact' })
          .eq('site_id', siteId)
          .or(`status.eq.published,and(status.eq.scheduled,scheduled_at.lte.${nowIso})`)
          .not('type_id', 'is', null)
          .eq('post_terms.term_id', categoryId)
          .order('published_at', { ascending: false })
          .range(from, to);
        if (error) throw error;
        rows = data ?? [];
        totalCount = total ?? rows.length;
      } else {
        const { data, error, count: total } = await supabase
          .from('posts')
          .select(baseCols, { count: 'exact' })
          .eq('site_id', siteId)
          .or(`status.eq.published,and(status.eq.scheduled,scheduled_at.lte.${nowIso})`)
          .not('type_id', 'is', null)
          .order('published_at', { ascending: false })
          .range(from, to);
        if (error) throw error;
        rows = data ?? [];
        totalCount = total ?? rows.length;
      }

      const thumbIds = rows.map((r) => r.thumbnail_id).filter((v): v is string => !!v);
      const mediaMap = new Map<string, string>();
      if (thumbIds.length > 0) {
        const { data: media } = await supabase
          .from('media_items')
          .select('id, storage_path, storage_provider, object_key, metadata')
          .in('id', thumbIds);
        (media ?? []).forEach((m) => mediaMap.set(m.id, getMediaPublicUrl(m)));
      }

      const items = rows.map((r) => {
        const seoImageUrl = getContentSeo(r.og).og_image || getContentSeo(r.seo).og_image;
        return {
          id: r.id,
          title: r.title,
          slug: r.slug,
          excerpt: r.excerpt?.trim() || getContentExcerpt(r.content),
          published_at: r.published_at,
          imageUrl: seoImageUrl || (r.thumbnail_id ? mediaMap.get(r.thumbnail_id) ?? getContentImage(r.content) : getContentImage(r.content)),
        };
      });

      return { items, totalCount };
    },
    enabled: !!siteId,
    refetchInterval: 60_000,
  });
}

const GRID_COLS: Record<2 | 3 | 4, string> = {
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
};

const IMAGE_FORMATS: Record<NonNullable<PostGridBlock['imageFormat']>, string> = {
  wide: 'aspect-video',
  square: 'aspect-square',
  portrait: 'aspect-[4/5]',
};

const IMAGE_RADII: Record<NonNullable<PostGridBlock['imageRadius']>, string> = {
  none: 'rounded-none',
  sm: 'rounded-lg',
  lg: 'rounded-2xl',
  full: 'rounded-full',
};

const BLOCK_RADII: Record<NonNullable<PostGridBlock['blockRadius']>, string> = {
  none: 'rounded-none',
  sm: 'rounded-lg',
  lg: 'rounded-2xl',
  full: 'rounded-full',
};

const LIST_IMAGE_SIZES: Record<NonNullable<PostGridBlock['imageFormat']>, string> = {
  wide: 'h-20 w-32',
  square: 'h-24 w-24',
  portrait: 'h-24 w-20',
};

const LEGACY_DEFAULT_POST_GRID_COLORS = new Set([
  '#004e98',
  '#0052a5',
  '#075985',
  '#0f172a',
  '#475569',
]);

function normalizeColor(value: string | null | undefined, fallback: string, { ignoreLegacy = false } = {}): string {
  const color = value?.trim();
  if (!color) return fallback;
  if (ignoreLegacy && LEGACY_DEFAULT_POST_GRID_COLORS.has(color.toLowerCase())) return fallback;
  return color;
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Placeholder visual quando o post não tem thumbnail. */
function Placeholder({ className }: { className?: string }) {
  return (
    <div
      className={className}
      style={{ background: 'linear-gradient(135deg, var(--color-primary), var(--color-accent))', opacity: 0.9 }}
    />
  );
}

interface PostGridViewProps {
  items: PostGridItem[];
  block: PostGridBlock;
  /** Base do link do site público (ex.: "site-teste"); ausente = cards não clicáveis (preview no editor). */
  siteSlug?: string;
  loading?: boolean;
  variant?: 'admin' | 'public';
  /** Paginação — omitida (undefined) esconde os controles; passe quando houver mais de 1 página. */
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
}

/** Grid apresentacional puro — reusado no preview do editor e no site público. */
export function PostGridView({ items, block, siteSlug, loading, variant = 'admin', page, totalPages, onPageChange }: PostGridViewProps) {
  const isPublic = variant === 'public';

  if (loading) {
    return (
      <div className="py-10 text-center text-sm" style={isPublic ? { color: 'var(--color-paragraph, #475569)' } : undefined}>
        Carregando posts…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div
        className={`rounded-2xl border border-dashed py-10 text-center text-sm ${isPublic ? '' : 'border-border text-muted'}`}
        style={isPublic ? { borderColor: 'rgba(15, 23, 42, 0.16)', color: 'var(--color-paragraph, #475569)' } : undefined}
      >
        Nenhum post publicado {block.categoryName ? `na categoria "${block.categoryName}"` : ''} para exibir.
      </div>
    );
  }

  const imageFormat = block.imageFormat ?? 'wide';
  const imageRadius = block.imageRadius ?? 'none';
  const blockRadius = block.blockRadius ?? 'none';
  const cardStyle: React.CSSProperties | undefined = isPublic
    ? {
        backgroundColor: normalizeColor(block.backgroundColor, 'var(--color-box-bg, #ffffff)', { ignoreLegacy: true }),
        borderColor: 'color-mix(in oklab, var(--color-primary), transparent 86%)',
      }
    : block.backgroundColor
      ? { backgroundColor: block.backgroundColor }
      : undefined;
  const titleStyle: React.CSSProperties | undefined = isPublic
    ? { color: normalizeColor(block.titleColor, 'var(--color-heading, #0f172a)', { ignoreLegacy: true }) }
    : block.titleColor
      ? { color: block.titleColor }
      : undefined;
  const textStyle: React.CSSProperties | undefined = isPublic
    ? { color: normalizeColor(block.textColor, 'var(--color-paragraph, #475569)', { ignoreLegacy: true }) }
    : block.textColor
      ? { color: block.textColor }
      : undefined;
  const overlayTitleStyle: React.CSSProperties | undefined = block.titleColor ? { color: block.titleColor } : undefined;
  const overlayTextStyle: React.CSSProperties | undefined = block.textColor ? { color: block.textColor } : undefined;
  const dateStyle: React.CSSProperties | undefined = isPublic
    ? { color: 'color-mix(in oklab, var(--color-accent, #d4af37), #0f172a 28%)' }
    : undefined;
  const overlayBackdropStyle: React.CSSProperties = {
    background: 'linear-gradient(to top, rgba(2,6,23,0.85), rgba(2,6,23,0.1) 60%, transparent)',
    backgroundColor: block.backgroundColor || undefined,
    backgroundBlendMode: block.backgroundColor ? 'multiply' : undefined,
  };

  const Wrapper = ({ item, children, className, style }: { item: PostGridItem; children: React.ReactNode; className: string; style?: React.CSSProperties }) =>
    siteSlug ? (
      <Link to={`/s/${siteSlug}/${item.slug}`} className={className} style={style}>{children}</Link>
    ) : (
      <div className={className} style={style}>{children}</div>
    );

  if (block.style === 'list') {
    return (
      <div>
        <div className="space-y-4">
          {items.map((item) => (
            <Wrapper
              key={item.id}
              item={item}
              className={`group flex gap-4 ${BLOCK_RADII[blockRadius]} border p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg ${isPublic ? '' : 'border-border bg-card'}`}
              style={cardStyle}
            >
              {block.showImage && (
                <div className={`${LIST_IMAGE_SIZES[imageFormat]} shrink-0 overflow-hidden ${IMAGE_RADII[imageRadius]}`}>
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.title} loading="lazy" className="h-full w-full object-cover" />
                  ) : (
                    <Placeholder className="h-full w-full" />
                  )}
                </div>
              )}
              <div className="min-w-0 flex-1">
                {block.showDate && formatDate(item.published_at) && (
                  <span className={`text-xs font-medium uppercase tracking-widest ${isPublic ? '' : 'text-muted'}`} style={dateStyle}>{formatDate(item.published_at)}</span>
                )}
                <h3 className={`mt-1 text-lg font-bold ${isPublic ? '' : 'text-ink'}`} style={titleStyle}>{item.title}</h3>
                {block.showExcerpt && item.excerpt && (
                  <p className={`mt-1 line-clamp-2 text-sm leading-relaxed ${isPublic ? '' : 'text-muted'}`} style={textStyle}>{item.excerpt}</p>
                )}
              </div>
            </Wrapper>
          ))}
        </div>
        {page !== undefined && totalPages !== undefined && onPageChange && (
          <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} isPublic={isPublic} />
        )}
      </div>
    );
  }

  if (block.style === 'overlay') {
    return (
      <div>
        <div className={`grid grid-cols-1 ${GRID_COLS[block.columns]} gap-6`}>
          {items.map((item) => (
            <Wrapper
              key={item.id}
              item={item}
              className={`group relative flex items-end overflow-hidden ${IMAGE_FORMATS[imageFormat]} ${BLOCK_RADII[blockRadius]} shadow-lg transition-all hover:-translate-y-1.5 hover:shadow-2xl`}
              style={cardStyle}
            >
              {item.imageUrl ? (
                <img src={item.imageUrl} alt={item.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              ) : (
                <Placeholder className="absolute inset-0 h-full w-full" />
              )}
              <div className="absolute inset-0" style={overlayBackdropStyle} />
              <div className="relative p-5">
                {block.showDate && formatDate(item.published_at) && (
                  <span className="text-xs font-medium uppercase tracking-widest text-white/70">{formatDate(item.published_at)}</span>
                )}
                <h3 className="mt-1 text-lg font-bold leading-snug text-white" style={overlayTitleStyle}>{item.title}</h3>
                {block.showExcerpt && item.excerpt && (
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-white/80" style={overlayTextStyle}>{item.excerpt}</p>
                )}
              </div>
            </Wrapper>
          ))}
        </div>
        {page !== undefined && totalPages !== undefined && onPageChange && (
          <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} isPublic={isPublic} />
        )}
      </div>
    );
  }

  // style === 'card'
  return (
    <div>
      <div className={`grid grid-cols-1 ${GRID_COLS[block.columns]} gap-6`}>
        {items.map((item) => (
          <Wrapper
            key={item.id}
            item={item}
            className={`group flex flex-col ${BLOCK_RADII[blockRadius]} border shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl ${isPublic ? '' : 'border-border bg-card hover:border-transparent'}`}
            style={cardStyle}
          >
            {block.showImage && (
              <div className={`${IMAGE_FORMATS[imageFormat]} w-full overflow-hidden ${IMAGE_RADII[imageRadius]}`}>
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt={item.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <Placeholder className="h-full w-full" />
                )}
              </div>
            )}
            <div className="flex flex-1 flex-col p-6">
              {block.showDate && formatDate(item.published_at) && (
                <span className={`text-xs font-medium uppercase tracking-widest ${isPublic ? '' : 'text-muted'}`} style={dateStyle}>{formatDate(item.published_at)}</span>
              )}
              <h3 className={`mt-2 text-xl font-bold ${isPublic ? '' : 'text-ink'}`} style={titleStyle}>{item.title}</h3>
              {block.showExcerpt && item.excerpt && (
                <p className={`mt-2 line-clamp-3 text-sm leading-relaxed ${isPublic ? '' : 'text-muted'}`} style={textStyle}>{item.excerpt}</p>
              )}
            </div>
          </Wrapper>
        ))}
      </div>
      {page !== undefined && totalPages !== undefined && onPageChange && (
        <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} isPublic={isPublic} />
      )}
    </div>
  );
}

/** Controles de paginação — mostrados só quando há mais de 1 página; "Mais antigos" avança para posts anteriores. */
function Pagination({
  page,
  totalPages,
  onPageChange,
  isPublic,
}: {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isPublic: boolean;
}) {
  if (totalPages <= 1) return null;

  const buttonClass = `inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
    isPublic ? '' : 'border-border hover:bg-surface hover:text-ink'
  }`;
  const buttonStyle: React.CSSProperties | undefined = isPublic ? { borderColor: 'rgba(15, 23, 42, 0.16)' } : undefined;

  return (
    <div
      className={`mt-8 flex items-center justify-center gap-4 ${isPublic ? '' : 'text-muted'}`}
      style={isPublic ? { color: 'var(--color-paragraph, #475569)' } : undefined}
    >
      <button type="button" onClick={() => onPageChange(page - 1)} disabled={page <= 1} className={buttonClass} style={buttonStyle}>
        <ChevronLeft className="h-4 w-4" /> Mais recentes
      </button>
      <span className="text-sm font-medium">Página {page} de {totalPages}</span>
      <button type="button" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages} className={buttonClass} style={buttonStyle}>
        Mais antigos <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Preview no editor admin — usa o site ativo (SiteProvider), cards não clicáveis. */
export function AdminPostGrid({ block }: { block: PostGridBlock }) {
  const { siteId } = useSite();
  const [page, setPage] = useState(1);
  const { data, isLoading } = usePostGridPosts(siteId, block.count, block.categoryId, page);
  const totalPages = Math.max(1, Math.ceil((data?.totalCount ?? 0) / Math.max(1, Math.min(block.count, 24))));
  return (
    <PostGridView
      items={data?.items ?? []}
      block={block}
      loading={isLoading}
      page={page}
      totalPages={totalPages}
      onPageChange={setPage}
    />
  );
}

/** Render no site público — recebe siteId/siteSlug explícitos (sem SiteProvider). */
export function PublicPostGrid({ block, siteId, siteSlug }: { block: PostGridBlock; siteId: string; siteSlug: string }) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = usePostGridPosts(siteId, block.count, block.categoryId, page);
  const totalPages = Math.max(1, Math.ceil((data?.totalCount ?? 0) / Math.max(1, Math.min(block.count, 24))));
  return (
    <section className="w-full py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-6">
        <PostGridView
          items={data?.items ?? []}
          block={block}
          siteSlug={siteSlug}
          loading={isLoading}
          variant="public"
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>
    </section>
  );
}

// ── Post em destaque (post_card) ───────────────────────────────────────
// Card de um único post escolhido pelo admin — mesma resolução de
// thumbnail/excerpt de usePostGridPosts, só que para um id específico em vez
// de uma página filtrada por categoria.

/** Busca um post específico do site pelo id, já com thumbnail/excerpt resolvidos. `null` = nenhum post selecionado ou post não encontrado/despublicado. */
export function usePostCardPost(siteId: string | null | undefined, postId: string | null | undefined) {
  return useQuery({
    queryKey: ['post-card', siteId, postId],
    queryFn: async (): Promise<PostGridItem | null> => {
      if (!siteId || !postId) return null;
      const { data: r, error } = await supabase
        .from('posts')
        .select('id, title, slug, excerpt, published_at, thumbnail_id, content, seo, og')
        .eq('site_id', siteId)
        .eq('id', postId)
        .maybeSingle();
      if (error) throw error;
      if (!r) return null;

      let thumbnailUrl: string | null = null;
      if (r.thumbnail_id) {
        const { data: media } = await supabase
          .from('media_items')
          .select('id, storage_path, storage_provider, object_key, metadata')
          .eq('id', r.thumbnail_id)
          .maybeSingle();
        if (media) thumbnailUrl = getMediaPublicUrl(media);
      }

      const seoImageUrl = getContentSeo(r.og).og_image || getContentSeo(r.seo).og_image;
      return {
        id: r.id,
        title: r.title,
        slug: r.slug,
        excerpt: r.excerpt?.trim() || getContentExcerpt(r.content),
        published_at: r.published_at,
        imageUrl: seoImageUrl || thumbnailUrl || getContentImage(r.content),
      };
    },
    enabled: !!siteId && !!postId,
  });
}

/** Apresentacional puro — reusado no preview do editor e no site público. */
function PostCardBlockView({ item, siteSlug, loading, isPublic }: { item: PostGridItem | null; siteSlug?: string; loading?: boolean; isPublic?: boolean }) {
  if (loading) {
    return <div className="py-10 text-center text-sm text-muted">Carregando post…</div>;
  }
  if (!item) {
    return (
      <div className="rounded-2xl border border-dashed border-border py-10 text-center text-sm text-muted">
        Nenhum post selecionado.
      </div>
    );
  }

  const Wrapper = ({ children, className }: { children: React.ReactNode; className: string }) =>
    siteSlug ? (
      <Link to={`/s/${siteSlug}/${item.slug}`} className={className}>{children}</Link>
    ) : (
      <div className={className}>{children}</div>
    );

  return (
    <Wrapper
      className={`group flex flex-col overflow-hidden rounded-2xl border shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${isPublic ? '' : 'border-border bg-card'}`}
    >
      <div className="aspect-video w-full overflow-hidden">
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <Placeholder className="h-full w-full" />
        )}
      </div>
      <div className="flex flex-1 flex-col p-6">
        <h3 className={`text-xl font-bold ${isPublic ? '' : 'text-ink'}`} style={isPublic ? { color: 'var(--color-heading, #0f172a)' } : undefined}>
          {item.title}
        </h3>
        {item.excerpt && (
          <p className={`mt-2 line-clamp-3 text-sm leading-relaxed ${isPublic ? '' : 'text-muted'}`} style={isPublic ? { color: 'var(--color-paragraph, #475569)' } : undefined}>
            {item.excerpt}
          </p>
        )}
      </div>
    </Wrapper>
  );
}

/** Preview no editor admin — usa o site ativo (SiteProvider), card não clicável. */
export function AdminPostCard({ block }: { block: PostCardBlock }) {
  const { siteId } = useSite();
  const { data, isLoading } = usePostCardPost(siteId, block.postId);
  return <PostCardBlockView item={data ?? null} loading={isLoading} />;
}

/** Render no site público — recebe siteId/siteSlug explícitos (sem SiteProvider). */
export function PublicPostCard({ block, siteId, siteSlug }: { block: PostCardBlock; siteId: string; siteSlug: string }) {
  const { data, isLoading } = usePostCardPost(siteId, block.postId);
  return (
    <section className="w-full py-8">
      <div className="mx-auto max-w-xl px-6">
        <PostCardBlockView item={data ?? null} siteSlug={siteSlug} loading={isLoading} isPublic />
      </div>
    </section>
  );
}
